/**
 * The site's routes, declared one by one (spec section 5).
 *
 * One route per country page, built from SUPPORTED_COUNTRIES less those held
 * back from the site (country/hiddenOnSite.ts), and no
 * `/$country` catch-all: an unknown one-segment path must still reach the
 * app's not-found page. Every page is a lazy chunk (SiteShell.tsx); this file
 * and that one are the only parts of the site the app shell loads, so they
 * import nothing heavier than the country list the shell already has.
 */
import type { ComponentType, ReactNode } from 'react';
import { createRoute, Navigate, type AnyRoute } from '@tanstack/react-router';
import { SUPPORTED_COUNTRIES } from '../shared/config/countryConfig';
import { HIDDEN_ON_SITE } from './country/hiddenOnSite';
import { PREVIEW_BASE, SITE_BASE, sitePaths } from './paths';
import { FromPreview, SitePage, TourEntryRoute } from './SiteShell';
import type { Door } from './tour/types';

const DOORS: Door[] = ['business', 'accountant', 'client'];


/**
 * The site's routes under `parent` (the root route). `HomeExtras` is what the
 * home carries that the site may not import itself, such as the referral
 * welcome, which reads the database.
 */
export function buildSiteRoutes(parent: AnyRoute, slots: { HomeExtras?: ComponentType } = {}): AnyRoute[] {
    const { HomeExtras } = slots;
    const extras = HomeExtras ? <HomeExtras /> : undefined;
    const at = (path: string, component: () => ReactNode) => createRoute({ getParentRoute: () => parent, path, component });

    const routes: AnyRoute[] = [
        createRoute({ getParentRoute: () => parent, path: `${SITE_BASE}/tour/$id`, component: TourEntryRoute }),
    ];
    // At the root, `/` belongs to the app's landing route, which signs a person in
    // or shows a guest the site's home (SiteRootPage). Under a prefix it is ours.
    if (SITE_BASE) routes.push(at(sitePaths.root(), () => <SitePage page="root" extras={extras} />));
    else {
        routes.push(at(PREVIEW_BASE, () => <Navigate to="/" replace />));
        routes.push(createRoute({ getParentRoute: () => parent, path: `${PREVIEW_BASE}/$`, component: FromPreview }));
    }
    for (const { code } of SUPPORTED_COUNTRIES) {
        if (HIDDEN_ON_SITE.includes(code)) continue; // held back from the site (siteCountries.ts)
        routes.push(at(sitePaths.home(code), () => <SitePage page="home" cc={code} extras={extras} />));
        for (const d of DOORS) routes.push(at(sitePaths.door(code, d), () => <SitePage page="door" cc={code} door={d} />));
        routes.push(at(sitePaths.everything(code), () => <SitePage page="everything" cc={code} />));
        routes.push(at(sitePaths.news(code), () => <SitePage page="new" cc={code} />));
    }
    if (import.meta.env.DEV) routes.push(at(`${SITE_BASE}/film-lab`, () => <SitePage page="film-lab" />));
    return routes;
}
